import { useEffect, useState } from "react";
import { AlertTriangle, Monitor } from "lucide-react";
import { Button } from "../../design-system";
import type { LibraryAdapter } from "./library-adapter";
import { libraryError } from "./library-runtime";
import "./execution-availability.css";

type Availability = {
  machines: { id: string; label: string }[] | null;
  error: string;
};
/** The adapter owns connection status. Loading and failure are never presented as zero PCs. */
export function useExecutionAvailability(adapter: LibraryAdapter) {
  const customerKey = JSON.stringify(
    adapter.customers.map((customer) => customer.id),
  );
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    adapter: LibraryAdapter;
    customerKey: string;
    attempt: number;
    data: Record<string, Availability>;
  } | null>(null);
  useEffect(() => {
    let live = true;
    const ids: string[] = JSON.parse(customerKey);
    for (const id of ids) {
      void Promise.resolve()
        .then(() => adapter.machines(id))
        .then(
          (machines): Availability => ({ machines, error: "" }),
          (error): Availability => ({
            machines: null,
            error: libraryError(error),
          }),
        )
        .then((result) => {
          if (!live) return;
          setState((previous) => ({
            adapter,
            customerKey,
            attempt,
            data: {
              ...(previous?.adapter === adapter &&
              previous.customerKey === customerKey &&
              previous.attempt === attempt
                ? previous.data
                : {}),
              [id]: result,
            },
          }));
        });
    }
    return () => {
      live = false;
    };
  }, [adapter, customerKey, attempt]);
  const current =
    state?.adapter === adapter &&
    state.customerKey === customerKey &&
    state.attempt === attempt;
  const data = current ? state.data : null;
  return {
    data,
    loading: !data || Object.keys(data).length < adapter.customers.length,
    retry: () => setAttempt((value) => value + 1),
  };
}
export function executionAvailabilityLabel(
  result: Availability | null | undefined,
) {
  return !result
    ? "PC 확인 중"
    : result.error
      ? "PC 확인 실패"
      : result.machines?.length
        ? `PC ${result.machines.length}대`
        : "연결된 PC 없음";
}
export function ExecutionAvailabilityNotice({
  result,
  onRetry,
}: {
  result: Availability | null | undefined;
  onRetry: () => void;
}) {
  if (result && !result.error && result.machines?.length) return null;
  const error = result?.error;
  return (
    <div
      className={`mce-availability ${error ? "is-error" : result ? "is-warning" : "is-loading"}`}
      role={error ? "alert" : "status"}
    >
      {result ? (
        <AlertTriangle size={17} aria-hidden="true" />
      ) : (
        <Monitor size={17} aria-hidden="true" />
      )}
      <div>
        <strong>
          {error
            ? "연결된 PC를 확인하지 못했습니다"
            : result
              ? "연결된 PC가 없어 배정 대기로 남습니다"
              : "연결된 PC를 확인하고 있습니다"}
        </strong>
        <p>
          {error ||
            (result
              ? "PC를 나중에 연결할 수 있습니다. 지금 작업을 등록해도 됩니다."
              : "확인이 끝나기 전에도 자동 배정으로 등록할 수 있습니다.")}
        </p>
      </div>
      {error && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          PC 다시 확인
        </Button>
      )}
    </div>
  );
}
